import { Injectable, inject } from '@angular/core';
import {
  ICreatePermission,
  IDiscoveryResult,
  IPermission,
  IPermissionFilter,
  IUpdatePermission,
} from '@nomoh-monorepo/shared-types';
import { PermissionsDataService } from './feature-permissions.dataservice.service';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private permissionsDataService = inject(PermissionsDataService);

  // PRINCÍPIO KISS: Component injeta DataService diretamente para signals
  // Service apenas para business logic com validações

  // ========================================
  // TAB 1: PERMISSÕES DESCOBERTAS
  // ========================================

  async carregarPermissions(): Promise<void> {
    // Business logic: Validar permissão (quando disponível)
    // await this.permissionService.validarPermissao('permissions.read');

    return this.permissionsDataService.carregarPermissions();
  }

  async buscarPermissions(filtros: Partial<IPermissionFilter>): Promise<void> {
    // await this.permissionService.validarPermissao('permissions.read');

    return this.permissionsDataService.buscar(filtros);
  }

  async forceRescan(): Promise<IDiscoveryResult | null> {
    // Business logic: Validar permissão crítica
    // await this.permissionService.validarPermissao('permissions.discover');

    return this.permissionsDataService.forceRescan();
  }

  async atualizarPermission(permission: IUpdatePermission): Promise<boolean> {
    // await this.permissionService.validarPermissao('permissions.update');

    // Validações de negócio
    if (!permission.id || permission.id.trim().length === 0) {
      throw new Error('ID da permissão é inválido');
    }

    return this.permissionsDataService.atualizarPermission(permission);
  }

  // ========================================
  // TAB 2: PERMISSÕES DE USUÁRIOS
  // ========================================

  async buscarPermissoesUsuario(userId: string): Promise<{
    fromPlan: IPermission[];
    extra: IPermission[];
  } | null> {
    // await this.permissionService.validarPermissao('user-permissions.read');

    // Validação de negócio
    if (!userId || userId.trim().length === 0) {
      throw new Error('ID do usuário é inválido');
    }

    return this.permissionsDataService.buscarPermissoesUsuario(userId);
  }

  async adicionarPermissoesExtras(
    userId: string,
    availabilityIds: string[]
  ): Promise<boolean> {
    // await this.permissionService.validarPermissao('user-permissions.create');

    // Validações de negócio
    if (!userId || userId.trim().length === 0) {
      throw new Error('ID do usuário é inválido');
    }

    if (!availabilityIds || availabilityIds.length === 0) {
      throw new Error('Nenhuma permissão selecionada');
    }

    return this.permissionsDataService.adicionarPermissoesExtras(
      userId,
      availabilityIds
    );
  }

  async removerPermissoesExtras(
    userId: string,
    permissionIds: string[]
  ): Promise<boolean> {
    // await this.permissionService.validarPermissao('user-permissions.delete');

    // Validações de negócio
    if (!userId || userId.trim().length === 0) {
      throw new Error('ID do usuário é inválido');
    }

    if (!permissionIds || permissionIds.length === 0) {
      throw new Error('Nenhuma permissão selecionada');
    }

    return this.permissionsDataService.removerPermissoesExtras(
      userId,
      permissionIds
    );
  }

  // ========================================
  // TAB 3: PERMISSÕES STANDALONE
  // ========================================

  async carregarPermissionsStandalone(): Promise<void> {
    // await this.permissionService.validarPermissao('permissions.read');

    return this.permissionsDataService.carregarPermissionsStandalone();
  }

  async criarPermissionStandalone(
    permission: ICreatePermission
  ): Promise<string | null> {
    // Business logic: Validar permissão crítica
    // await this.permissionService.validarPermissao('permissions.create');

    // Validações de negócio
    if (!permission.key || permission.key.trim().length === 0) {
      throw new Error('Chave da permissão é obrigatória');
    }

    if (!permission.module || permission.module.trim().length === 0) {
      throw new Error('Módulo da permissão é obrigatório');
    }

    if (!permission.action || permission.action.trim().length === 0) {
      throw new Error('Ação da permissão é obrigatória');
    }

    if (!permission.description || permission.description.trim().length === 0) {
      throw new Error('Descrição da permissão é obrigatória');
    }

    // Validar formato da key (Sistema.module.action)
    const keyParts = permission.key.split('.');
    if (keyParts.length !== 3) {
      throw new Error(
        'Chave da permissão deve estar no formato: Sistema.module.action'
      );
    }

    return this.permissionsDataService.criarPermissionStandalone(permission);
  }

  async atualizarPermissionStandalone(
    permission: IUpdatePermission
  ): Promise<boolean> {
    // await this.permissionService.validarPermissao('permissions.update');

    // Validações de negócio
    if (!permission.id || permission.id.trim().length === 0) {
      throw new Error('ID da permissão é inválido');
    }

    if (permission.key) {
      const keyParts = permission.key.split('.');
      if (keyParts.length !== 3) {
        throw new Error(
          'Chave da permissão deve estar no formato: Sistema.module.action'
        );
      }
    }

    return this.permissionsDataService.atualizarPermissionStandalone(
      permission
    );
  }

  async excluirPermissionStandalone(id: string): Promise<boolean> {
    // await this.permissionService.validarPermissao('permissions.delete');

    // Validação de negócio
    if (!id || id.trim().length === 0) {
      throw new Error('ID da permissão é inválido');
    }

    return this.permissionsDataService.excluirPermissionStandalone(id);
  }

  // ========================================
  // MÉTODOS DE NAVEGAÇÃO
  // ========================================

  async proximaPagina(): Promise<void> {
    return this.permissionsDataService.proximaPagina();
  }

  async paginaAnterior(): Promise<void> {
    return this.permissionsDataService.paginaAnterior();
  }

  async irParaPagina(pagina: number): Promise<void> {
    return this.permissionsDataService.irParaPagina(pagina);
  }

  async limparFiltros(): Promise<void> {
    return this.permissionsDataService.limparFiltros();
  }

  // ========================================
  // PERMISSÕES DISPONÍVEIS
  // ========================================

  async buscarPermissoesDisponiveis(filter: {
    type?: 'plan' | 'standalone';
    sistemaId?: string;
  }): Promise<any[]> {
    // await this.permissionService.validarPermissao('permissions.read');

    return this.permissionsDataService.buscarPermissoesDisponiveis(filter);
  }
}
